import { useNavigate, useSearchParams } from 'react-router-dom'
import { useCallback, useEffect, useMemo } from 'react'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { useInitialTableSort } from 'app/hooks/useInitialTableSort'
import { useIsOrgUser } from 'app/hooks/useIsOrgUser'
import { useCheckPermission } from 'app/hooks/useCheckPermission'
import type { UserJson } from 'app/types/entities'
import { defaultColumns, filterableColumns } from 'app/table-columns/users'
import { legacyRoleRank } from 'app/types/roles'
import { hasRole, useUser } from 'app/utils'
import { useUserStore } from 'app/stores/StoreContext'

export type UseUsersIndexReturn = {
  users: { hits: UserJson[]; total: number } | undefined
  me: UserJson
  columns: ReturnType<typeof defaultColumns>
  filters: ReturnType<typeof filterableColumns>
  initialSort: ReturnType<typeof useInitialTableSort>
  canEditUser: boolean
  userIsOrg: boolean
  staffV2Enabled: boolean
  onSearchValueChange: (value: string) => void
}

export function useUsersIndex(): UseUsersIndexReturn {
  const [searchParams] = useSearchParams()
  const sort = searchParams.get(`sort`) === `` ? undefined : (searchParams.get(`sort`) ?? `name.keyword`)
  const page = Number(searchParams.get(`page`) || 0)
  const query = searchParams.get(`query`) ?? ``

  const userStore = useUserStore()
  const me = useUser()

  const staffV2Enabled = useFeatureToggle(`staff-v2`)
  const isOrgUser = useIsOrgUser()
  const checkPermission = useCheckPermission()
  const navigate = useNavigate()

  const userIsOrg = staffV2Enabled ? isOrgUser : hasRole(me!, `orgUser`)
  const canEditUser = staffV2Enabled
    ? checkPermission([`org:settings:manage-users`, `users:manage`])
    : hasRole(me!, `workspaceAdmin`)

  const initialSort = useInitialTableSort(sort)

  useEffect(() => {
    const [field, direction] = sort?.split(`:`) ?? []
    const orgScope = !me?.workspaceId ? `role:[${legacyRoleRank.orgUser} TO ${legacyRoleRank.orgAdmin}]` : ``
    const search = [orgScope, query].filter(Boolean).join(` `) || undefined
    userStore.fetchList({
      search,
      pagination: { page, limit: 500 },
      sort: field ? { field, direction: (direction?.toUpperCase() as 'ASC' | 'DESC') ?? 'ASC' } : undefined,
    })
  }, [sort, page, query, me?.workspaceId])

  const columns = useMemo(
    () => defaultColumns({ me: me!, canManageUsers: canEditUser, isOrgUser: userIsOrg, navigate }),
    [me, canEditUser, userIsOrg, navigate],
  )

  const filters = useMemo(() => filterableColumns({ showLastLoginDate: true }), [])

  const users = userStore.list as { hits: UserJson[]; total: number } | undefined

  const onSearchValueChange = useCallback(
    (value: string) => {
      const params = new URLSearchParams(searchParams)
      if (value) params.set(`query`, value)
      else params.delete(`query`)
      navigate(`?${params.toString()}`, { replace: true })
    },
    [searchParams, navigate],
  )

  return { users, me: me!, columns, filters, initialSort, canEditUser, userIsOrg, staffV2Enabled, onSearchValueChange }
}
